import React, { useMemo, useState } from "react";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { useQuery } from "react-query";
import moment from "moment";
import AssetMetricsReadDto from "../../../../Models/Dashboard/AssetMetricsReadDto";
import InsightsAnomaliesView from "../Components/Insights/InsightsAnomaliesView";
import type { InsightTableInput } from "../Components/Insights/types";
import LoadingSpinner from "../../../Shared/Components/LoadingSpinner";
import anomalyApiService from "../Utilities/AnomalyApiService";
import siteExplorerApiService from "../../../SiteExplorer/Utilities/SiteExplorerApiService";
import devicesApiService from "../../../Devices/Utilities/DevicesApiService";
import { ReactQueryKeys } from "../../../../Constants/ClientRoutingConstants";

interface Props {
  assets: AssetMetricsReadDto[];
}

export default function SiteInsightsTab({ assets }: Props) {
  const [dateFilter, setDateFilter] = useState({
    endDate: moment().endOf("day").toDate(),
    startDate: moment().subtract(7, "days").startOf("day").toDate(),
  });

  const handleStartDateChange = (event: any) => {
    if (event.value) {
      setDateFilter({
        ...dateFilter,
        startDate: moment(event.value).startOf("day").toDate(),
      });
    }
  };

  const handleEndDateChange = (event: any) => {
    if (event.value) {
      setDateFilter({
        ...dateFilter,
        endDate: moment(event.value).endOf("day").toDate(),
      });
    }
  };

  const assetNamesById = useMemo(
    () => Object.fromEntries(assets.map((asset) => [asset.id, asset.name])),
    [assets]
  );

  // Rows only carry pointId; one site-wide call resolves the endpoint names.
  const endpointsQuery = useQuery(ReactQueryKeys.SiteInsightsEndpointsQuery, () => siteExplorerApiService.getAllEndpoints());
  const pointNamesById = useMemo(
    () => Object.fromEntries((endpointsQuery.data ?? []).map((endpoint) => [endpoint.id, endpoint.name])),
    [endpointsQuery.data]
  );

  // Device rows (e.g. gateways) carry deviceId instead; devices are labelled by serial.
  const devicesQuery = useQuery(ReactQueryKeys.SiteInsightsDevicesQuery, () => devicesApiService.getAllDevices());
  const deviceNamesById = useMemo(
    () => Object.fromEntries((devicesQuery.data ?? []).map((device) => [device.id, device.serialNumber])),
    [devicesQuery.data]
  );

  // Range filtering happens server-side (overlap semantics, ongoing included).
  const insightsQuery = useQuery<InsightTableInput[]>(
    ["siteInsightsAnomalies", dateFilter.startDate, dateFilter.endDate],
    async () => {
      const [anomalies, anchors] = await Promise.all([
        anomalyApiService.getSiteAnomaliesPayload(dateFilter.startDate, dateFilter.endDate),
        anomalyApiService.getSiteAnchorsPayload(dateFilter.startDate, dateFilter.endDate),
      ]);

      return [
        ...anomalies.map((anomaly) => ({ kind: "anomaly" as const, data: anomaly })),
        ...anchors.map((anchor) => ({ kind: "anchor" as const, data: anchor })),
      ];
    }
  );


  return (
    <div className="flex-1 min-h-0 flex flex-col p-4 m-2 bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="mb-2 flex flex-wrap items-center justify-end gap-3">
        <div className="flex items-center gap-x-2">
          <DatePicker
            value={dateFilter.startDate}
            onChange={handleStartDateChange}
            max={dateFilter.endDate || undefined}
            width="150px"
          />
          <span>-</span>
          <DatePicker
            value={dateFilter.endDate}
            onChange={handleEndDateChange}
            min={dateFilter.startDate || undefined}
            max={moment().toDate()}
            width="150px"
          />
        </div>
      </div>
      <div className="flex-1 min-h-0" style={{ minHeight: 200 }}>
        {insightsQuery.isLoading ? (
          <div className="flex items-center justify-center h-full min-h-[200px]">
            <LoadingSpinner />
          </div>
        ) : (
          <InsightsAnomaliesView
            data={insightsQuery.data ?? []}
            assetNamesById={assetNamesById}
            pointNamesById={pointNamesById}
            deviceNamesById={deviceNamesById}
          />
        )}
      </div>
    </div>
  );
}
